import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Archive,
  BookOpenText,
  CheckCircle,
  Eye,
  FileArrowUp,
  FileText,
  Folders,
  Link,
  MagnifyingGlass,
  PencilSimpleLine,
  Plus,
  Trash,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";

import { API, publicUrl } from "../lib/api";

const contentTypes = ["Documento", "Procedimento", "Formulario", "Noticia"];
const folderOptions = ["Beneficios", "Admissoes", "Treinamentos", "Comunicados", "Procedimentos", "Geral"];
const audienceOptions = [
  { id: "colaboradores", title: "Colaboradores", helper: "Aparece para colaboradores autorizados." },
  { id: "gerentes", title: "Gerentes", helper: "Aparece em Conteudos da Gerencia." },
  { id: "autores", title: "Autores da area", helper: "Apoia revisao e manutencao do setor." },
  { id: "privado", title: "Privado do setor", helper: "Nao aparece para leitura geral." },
];

const emptyForm = {
  title: "",
  content: "",
  content_type: "Documento",
  folder: "Geral",
  source: "link",
  url: "",
  file_url: "",
  file_name: "",
  audiences: ["colaboradores", "gerentes"],
  status: "draft",
};

const statusLabels = {
  published: "Publicado",
  draft: "Rascunho",
  archived: "Arquivado",
};

const statusStyles = {
  published: "bg-emerald-100 text-emerald-700",
  draft: "bg-amber-100 text-amber-700",
  archived: "bg-slate-100 text-slate-600",
};

function formatDate(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return value;
  }
}

function normalizeForm(item) {
  return {
    ...emptyForm,
    ...item,
    audiences: item?.audiences?.length ? item.audiences : emptyForm.audiences,
  };
}

function ContentPanel({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(editing ? normalizeForm(editing) : emptyForm);
    setFile(null);
    setError("");
  }, [editing, open]);

  if (!open) return null;

  const update = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const toggleAudience = (id) => {
    setForm(current => {
      const has = current.audiences.includes(id);
      return {
        ...current,
        audiences: has ? current.audiences.filter(item => item !== id) : [...current.audiences, id],
      };
    });
  };

  const save = async (status) => {
    if (!form.title.trim()) {
      setError("Informe um titulo.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      let payload = { ...form, status };
      if (form.source === "upload" && file) {
        const fileData = new FormData();
        fileData.append("file", file);
        const { data } = await axios.post(`${API}/area-contents/upload`, fileData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        payload = { ...payload, file_url: data.file_url, file_name: data.file_name, url: "" };
      }

      if (editing?.item_id) {
        await axios.put(`${API}/area-contents/${editing.item_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/area-contents`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Nao foi possivel salvar o conteudo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/35 backdrop-blur-sm">
      <button type="button" aria-label="Fechar painel" className="hidden flex-1 cursor-default lg:block" onClick={onClose} />
      <aside className="flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-5">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1.5 text-xs font-semibold text-[#2346ff]">
              <Plus className="h-4 w-4" weight="regular" />
              {editing ? "Editar conteudo" : "Novo conteudo"}
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-950">Alimentar conteudo da area</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">Defina o material, a pasta e quem podera visualizar.</p>
          </div>
          <button type="button" aria-label="Fechar" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" weight="regular" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

          <div className="grid gap-5">
            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="mb-4 text-sm font-bold text-slate-900">Dados do conteudo</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <select value={form.content_type} onChange={event => update("content_type", event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm">
                    {contentTypes.map(type => <option key={type}>{type}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Pasta</Label>
                  <select value={form.folder} onChange={event => update("folder", event.target.value)} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm">
                    {folderOptions.map(folder => <option key={folder}>{folder}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <Label>Titulo</Label>
                <Input value={form.title} onChange={event => update("title", event.target.value)} placeholder="Ex.: Politica de beneficios 2026" className="h-11 rounded-xl border-slate-200 bg-white" />
              </div>

              <div className="mt-4 space-y-2">
                <Label>Descricao</Label>
                <Textarea value={form.content} onChange={event => update("content", event.target.value)} placeholder="Explique o que este conteudo orienta ou comunica." className="min-h-24 rounded-xl border-slate-200 bg-white text-sm" />
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="mb-4 text-sm font-bold text-slate-900">Arquivo ou link</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <button type="button" onClick={() => update("source", "upload")} className={`flex gap-3 rounded-2xl border p-4 text-left ${form.source === "upload" ? "border-[#cfd7ff] bg-[#f7f8ff]" : "border-slate-200 bg-white"}`}>
                  <UploadSimple className="h-5 w-5 text-[#2346ff]" weight="regular" />
                  <span><span className="block text-sm font-bold">Enviar arquivo</span><span className="text-xs text-slate-500">PDF, Word, Excel ou imagem</span></span>
                </button>
                <button type="button" onClick={() => update("source", "link")} className={`flex gap-3 rounded-2xl border p-4 text-left ${form.source === "link" ? "border-[#cfd7ff] bg-[#f7f8ff]" : "border-slate-200 bg-white"}`}>
                  <Link className="h-5 w-5 text-[#2346ff]" weight="regular" />
                  <span><span className="block text-sm font-bold">Usar link</span><span className="text-xs text-slate-500">Drive, formulario ou pagina</span></span>
                </button>
              </div>

              {form.source === "upload" ? (
                <label className="mt-4 flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-[#cfd7ff] bg-[#f7f8ff] px-4 py-5 text-center">
                  <UploadSimple className="mb-2 h-6 w-6 text-[#2346ff]" weight="regular" />
                  <span className="text-sm font-bold text-slate-900">{file?.name || form.file_name || "Clique para selecionar um arquivo"}</span>
                  <span className="mt-1 text-xs text-slate-500">Limite atual: 25MB</span>
                  <input type="file" className="sr-only" onChange={event => setFile(event.target.files?.[0] || null)} />
                </label>
              ) : (
                <div className="mt-4 space-y-2">
                  <Label>Link do conteudo</Label>
                  <Input value={form.url || ""} onChange={event => update("url", event.target.value)} placeholder="https://drive.google.com/..." className="h-11 rounded-xl border-slate-200 bg-white" />
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="mb-4 text-sm font-bold text-slate-900">Quem pode visualizar</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {audienceOptions.map(option => {
                  const active = form.audiences.includes(option.id);
                  return (
                    <button key={option.id} type="button" onClick={() => toggleAudience(option.id)} className={`min-h-24 rounded-2xl border p-4 text-left ${active ? "border-[#cfd7ff] bg-[#f7f8ff]" : "border-slate-200 bg-white"}`}>
                      <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
                        {option.title}
                        {active && <Badge className="border-0 bg-[#2346ff] text-[10px] text-white">Selecionado</Badge>}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500">{option.helper}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </div>

        <div className="border-t border-slate-100 bg-white px-5 py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" className="h-11 rounded-xl border-slate-200 text-slate-700" disabled={saving} onClick={() => save("draft")}>
              Salvar rascunho
            </Button>
            <Button variant="outline" className="h-11 rounded-xl border-slate-200 text-slate-700" disabled={saving} onClick={() => save("archived")}>
              Arquivar
            </Button>
            <Button className="h-11 rounded-xl bg-[#2346ff] text-white hover:bg-[#1d3ace]" disabled={saving} onClick={() => save("published")}>
              {saving ? "Salvando..." : "Publicar"}
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function ContentManagementPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/area-contents?scope=mine`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || "Nao foi possivel carregar os conteudos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(item =>
      [item.title, item.content, item.folder, item.content_type, item.department]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [items, query]);

  const stats = useMemo(() => {
    return {
      published: items.filter(item => item.status === "published").length,
      managers: items.filter(item => item.audiences?.includes("gerentes")).length,
      draft: items.filter(item => item.status === "draft").length,
      archived: items.filter(item => item.status === "archived").length,
    };
  }, [items]);

  const openNew = () => {
    setEditing(null);
    setPanelOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setPanelOpen(true);
  };

  const remove = async (item) => {
    if (!window.confirm("Excluir este conteudo?")) return;
    await axios.delete(`${API}/area-contents/${item.item_id}`, { withCredentials: true });
    await loadItems();
  };

  const openContent = (item) => {
    const target = item.source === "upload" ? item.file_url : item.url;
    if (target) window.open(target.startsWith("/api") ? publicUrl(target) : target, "_blank", "noopener,noreferrer");
  };

  const saved = async () => {
    setPanelOpen(false);
    setEditing(null);
    await loadItems();
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#eef1ff] px-3 py-1.5 text-xs font-semibold text-[#2346ff]">
            <Folders className="h-4 w-4" weight="regular" />
            Dashboard do Autor da Area
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-950">Gestao de Conteudo</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Publique materiais do setor {user?.department ? `(${user.department})` : ""} e controle a visibilidade por perfil.
          </p>
        </div>

        <Button className="h-11 rounded-xl bg-[#2346ff] px-4 text-white hover:bg-[#1d3ace]" onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" weight="regular" />
          Criar conteudo
        </Button>
      </div>

      {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Publicados", value: stats.published, icon: CheckCircle },
          { label: "Para gerentes", value: stats.managers, icon: Eye },
          { label: "Rascunhos", value: stats.draft, icon: PencilSimpleLine },
          { label: "Arquivados", value: stats.archived, icon: Archive },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bento-card flex items-center gap-3 p-4">
            <div className="soft-icon h-11 w-11"><Icon className="h-5 w-5" weight="regular" /></div>
            <div>
              <p className="font-heading text-2xl font-bold text-slate-950">{value}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <section className="bento-card p-5">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold text-slate-900">Conteudos da area</h2>
            <p className="mt-1 text-xs text-slate-500">Tudo que for publicado aqui passa a alimentar a intranet real.</p>
          </div>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#2346ff]" weight="regular" />
            <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Buscar conteudo" className="h-10 rounded-xl border-slate-200 bg-slate-50 pl-10 sm:w-72" />
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Carregando conteudos...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center">
            <FileArrowUp className="mx-auto mb-3 h-8 w-8 text-[#2346ff]" weight="regular" />
            <p className="font-semibold text-slate-800">Nenhum conteudo cadastrado ainda.</p>
            <p className="mt-1 text-sm text-slate-500">Crie o primeiro material da area para alimentar a intranet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="pb-3 font-bold">Conteudo</th>
                  <th className="pb-3 font-bold">Tipo</th>
                  <th className="pb-3 font-bold">Pasta</th>
                  <th className="pb-3 font-bold">Visibilidade</th>
                  <th className="pb-3 font-bold">Status</th>
                  <th className="pb-3 font-bold">Atualizacao</th>
                  <th className="pb-3 font-bold">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.item_id} className="border-b border-slate-100 last:border-0">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <FileText className="h-5 w-5 flex-shrink-0 text-[#2346ff]" weight="regular" />
                        <span>{item.title}</span>
                      </div>
                      {item.content && <p className="mt-1 max-w-sm truncate text-xs text-slate-500">{item.content}</p>}
                    </td>
                    <td className="py-3 pr-4 text-sm text-slate-500">{item.content_type}</td>
                    <td className="py-3 pr-4 text-sm text-slate-500">{item.folder}</td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(item.audiences || []).map(audience => <Badge key={audience} className="bg-[#eef1ff] text-[#2346ff]">{audience}</Badge>)}
                      </div>
                    </td>
                    <td className="py-3 pr-4"><Badge className={`border-0 text-xs ${statusStyles[item.status] || statusStyles.draft}`}>{statusLabels[item.status] || item.status}</Badge></td>
                    <td className="py-3 pr-4 text-sm text-slate-500">{formatDate(item.updated_at)}</td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openContent(item)} disabled={!item.url && !item.file_url}>
                          <Eye className="h-4 w-4" weight="regular" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openEdit(item)}>
                          <PencilSimpleLine className="h-4 w-4" weight="regular" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-xl text-rose-600" onClick={() => remove(item)}>
                          <Trash className="h-4 w-4" weight="regular" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ContentPanel open={panelOpen} editing={editing} onClose={() => setPanelOpen(false)} onSaved={saved} />
    </div>
  );
}
